import { Check, Crosshair, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import type { ImportedPieceView } from '@obraplan/core/application/contracts/takeoff';
import {
  IMPORTED_PIECE_KIND_LABEL,
  type ImportedPieceKind,
  type ImportedPieceStatus,
} from '@obraplan/core/domain/takeoff/imported-piece';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { COR_DA_EXECUCAO } from './pecas-da-planilha';
import { metresToPixel } from './plan-tracer';

/**
 * As peças da planilha na planta (Fase 28, etapa 3 — RN-427 a RN-430).
 *
 * Duas partes, como as outras peças da prancha:
 *
 * - **a lista da ferramenta**: com a Estaca, o Bloco, o Muro… escolhidos, as peças importadas
 *   daquele tipo, a que já tem lugar e a que falta. "Posicionar na planta" deixa o próximo
 *   clique da prancha para ela — o mesmo gesto do "Delimitar na planta" do ambiente
 *   cadastrado sem contorno;
 * - **o desenho**: um marcador com o nome em cada peça posicionada, pintado pela execução —
 *   cinza, não iniciada; âmbar, em execução; verde, executada (RN-430).
 *
 * Na lista, o lápis dá à peça um nome novo (20/10/2026, RN-457): o elemento mudou de nome no
 * projeto depois de confirmado o quantitativo, e é na hora de posicioná-lo que isso aparece. O
 * nome da planilha fica embaixo, e a reimportação continua achando a peça por ele (RN-458).
 *
 * O marcador identifica, não mede: o tamanho é fixo por tipo, e a quantidade continua a da
 * planilha. As cores não seguem o tema, como o resto do traçado — a prancha é papel branco.
 */

const ROTULO_DA_EXECUCAO: Readonly<Record<ImportedPieceStatus, string>> = {
  NAO_INICIADA: 'não iniciada',
  EM_EXECUCAO: 'em execução',
  EXECUTADA: 'executada',
};

/** O tamanho do marcador, em metros: só para a peça caber à vista, sem pretender medir. */
const TAMANHO_DO_MARCADOR: Readonly<Record<ImportedPieceKind, number>> = {
  ESTACA: 0.5,
  BLOCO: 0.9,
  BALDRAME: 0.7,
  ARRANQUE: 0.5,
  MURO: 0.7,
  PILAR_CONTENCAO: 0.6,
  OUTRA: 0.5,
};

/** A partir de quantas peças a lista ganha a busca, como a das peças do dia. */
const BUSCA_A_PARTIR_DE = 12;

/** "b 92" acha "B92": a busca ignora caixa, acento e espaço. */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '').toLowerCase();
}

function execucaoDe(peca: ImportedPieceView): string {
  if (peca.status === 'NAO_INICIADA') return ROTULO_DA_EXECUCAO.NAO_INICIADA;
  return `${ROTULO_DA_EXECUCAO[peca.status]} (${String(peca.executedServices)} de ${String(peca.services)} serviços)`;
}

// ── O desenho ───────────────────────────────────────────────────────────────────

export function PecasDaPlanilhaDesenhadas({
  pecas,
  escala,
  zoom,
  destaque,
  posicionando,
  clicaveis = false,
}: {
  pecas: readonly ImportedPieceView[];
  escala: number;
  zoom: number;
  /** O tipo da ferramenta escolhida: as peças dele ficam em evidência. */
  destaque: ImportedPieceKind | undefined;
  /** A peça que está sendo posicionada — o marcador dela fica tracejado até o clique. */
  posicionando: string | undefined;
  /**
   * O marcador do tipo da ferramenta aceita o clique, que o pega para mover (quem trata é a
   * prancha, pelo `data-peca-da-planilha`). Os outros deixam o clique passar para o traço.
   */
  clicaveis?: boolean;
}) {
  return (
    <g data-pecas-da-planilha="">
      {pecas.map((peca) => {
        if (peca.position === undefined) return null;
        const centro = metresToPixel(peca.position, escala);
        const meio = Math.max(TAMANHO_DO_MARCADOR[peca.kind] / 2 / escala, 6 / zoom);
        const cor = COR_DA_EXECUCAO[peca.status];
        const apagada = destaque !== undefined && destaque !== peca.kind;
        const emMovimento = posicionando === peca.name;
        const fonte = Math.min(Math.max(meio * 0.75, 9 / zoom), 14 / zoom);
        const traco = {
          fill: cor,
          fillOpacity: peca.status === 'NAO_INICIADA' ? 0.25 : 0.6,
          stroke: cor,
          strokeWidth: 1.5 / zoom,
          ...(emMovimento ? { strokeDasharray: `${4 / zoom} ${3 / zoom}` } : {}),
        };

        return (
          <g
            key={peca.name}
            data-peca-da-planilha={peca.name}
            data-execucao={peca.status}
            opacity={apagada ? 0.35 : 1}
            className={
              clicaveis && peca.kind === destaque ? 'cursor-pointer' : 'pointer-events-none'
            }
          >
            <title>{`${peca.name} — ${execucaoDe(peca)}`}</title>
            {peca.kind === 'BLOCO' || peca.kind === 'PILAR_CONTENCAO' ? (
              <rect
                x={centro.x - meio}
                y={centro.y - meio}
                width={meio * 2}
                height={meio * 2}
                {...traco}
              />
            ) : peca.kind === 'ARRANQUE' ? (
              <polygon
                points={`${String(centro.x)},${String(centro.y - meio)} ${String(centro.x + meio)},${String(centro.y)} ${String(centro.x)},${String(centro.y + meio)} ${String(centro.x - meio)},${String(centro.y)}`}
                {...traco}
              />
            ) : (
              <circle cx={centro.x} cy={centro.y} r={meio} {...traco} />
            )}
            <text
              x={centro.x}
              y={centro.y - meio - fonte * 0.4}
              textAnchor="middle"
              fontSize={fonte}
              fontWeight={600}
              fill="#0f172a"
              stroke="#ffffff"
              strokeWidth={fonte * 0.22}
              paintOrder="stroke"
              className="select-none"
            >
              {peca.name}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// ── A lista da ferramenta ───────────────────────────────────────────────────────

export function PecasDaPlanilhaDaFerramenta({
  tipo,
  pecas,
  posicionando,
  ocupado,
  erro,
  semEscala,
  onPosicionar,
  onCancelar,
  onTirar,
  onRenomear,
}: {
  tipo: ImportedPieceKind;
  /** Todas as peças importadas da tipologia; a lista recorta as do tipo. */
  pecas: readonly ImportedPieceView[];
  posicionando: string | undefined;
  ocupado: boolean;
  erro?: string | undefined;
  /** Sem a escala da planta não há onde pôr ponto nenhum. */
  semEscala: boolean;
  onPosicionar: (nome: string) => void;
  onCancelar: () => void;
  onTirar: (nome: string) => void;
  /** Grava o nome novo; responde com ele quando gravou. Ausente: a lista não renomeia. */
  onRenomear?: ((nome: string, novo: string) => Promise<string | undefined>) | undefined;
}) {
  const [busca, setBusca] = useState('');
  /** A peça cujo nome está sendo trocado — uma por vez — e o que já foi digitado. */
  const [renomeando, setRenomeando] = useState<string | undefined>(undefined);
  const [nomeNovo, setNomeNovo] = useState('');
  const doTipo = pecas.filter((peca) => peca.kind === tipo);
  if (doTipo.length === 0) return null;

  const faltam = doTipo.filter((peca) => peca.position === undefined).length;
  const comBusca = doTipo.length > BUSCA_A_PARTIR_DE;
  const termo = normalizar(busca);
  const visiveis =
    comBusca && termo !== ''
      ? doTipo.filter((peca) => normalizar(peca.name).includes(termo))
      : doTipo;
  const rotulo = IMPORTED_PIECE_KIND_LABEL[tipo];

  return (
    <section
      aria-label={`${rotulo} da planilha`}
      className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3"
    >
      <h3 className="text-sm font-semibold">
        {rotulo} da planilha
        <span className="font-normal text-(--color-content-muted)">
          {` · ${String(doTipo.length)}${faltam > 0 ? ` · faltam ${String(faltam)} na planta` : ' · todas na planta'}`}
        </span>
      </h3>

      {posicionando !== undefined ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-(--color-surface-muted) p-2 text-sm">
          <Crosshair className="size-4" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            Clique na planta onde fica <strong>{posicionando}</strong>. Depois dela vem a próxima da
            fila — Cancelar para parar.
          </span>
          <Button size="sm" variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      ) : (
        <p className="text-xs text-(--color-content-muted)">
          O ponto só identifica a peça na planta: a quantidade continua a da planilha. Clique num
          marcador para movê-lo.
        </p>
      )}
      {semEscala && (
        <p className="text-xs text-(--color-state-risk)">
          Calibre a escala da planta antes de posicionar as peças.
        </p>
      )}

      {comBusca && (
        <Input
          type="search"
          aria-label={`Buscar em ${rotulo.toLowerCase()} da planilha`}
          placeholder="Buscar pelo nome — ex.: B92"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
      )}

      {visiveis.length === 0 ? (
        <p className="text-xs text-(--color-content-muted)">Nenhuma peça com “{busca.trim()}”.</p>
      ) : (
        <ul className={`flex flex-col gap-1 ${comBusca ? 'max-h-80 overflow-y-auto' : ''}`}>
          {visiveis.map((peca) => {
            const naPlanta = peca.position !== undefined;
            if (renomeando === peca.name && onRenomear !== undefined) {
              return (
                <li key={peca.name} className="rounded-md px-1 py-1 text-sm">
                  <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(evento) => {
                      evento.preventDefault();
                      void onRenomear(peca.name, nomeNovo).then((novo) => {
                        if (novo !== undefined) setRenomeando(undefined);
                      });
                    }}
                  >
                    <Input
                      aria-label={`Nome novo de ${peca.name}`}
                      className="min-w-0 flex-1"
                      value={nomeNovo}
                      maxLength={120}
                      autoFocus
                      onChange={(evento) => setNomeNovo(evento.target.value)}
                      onKeyDown={(evento) => {
                        if (evento.key === 'Escape') setRenomeando(undefined);
                      }}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      variant="primary"
                      disabled={ocupado || nomeNovo.trim() === ''}
                    >
                      <Check className="size-4" aria-hidden="true" />
                      Salvar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setRenomeando(undefined)}
                    >
                      Cancelar
                    </Button>
                    <p className="w-full text-xs text-(--color-content-muted)">
                      Muda em todos os serviços da peça, no ponto da planta e nas marcas de
                      execução. A planilha continua achando a peça pelo nome dela.
                    </p>
                  </form>
                </li>
              );
            }
            return (
              <li
                key={peca.name}
                className="flex flex-wrap items-center gap-2 rounded-md px-1 py-1 text-sm"
              >
                <span
                  aria-hidden="true"
                  className="inline-block size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: COR_DA_EXECUCAO[peca.status] }}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{peca.name}</span>
                  <span className="block text-xs text-(--color-content-muted)">
                    {naPlanta ? 'na planta' : 'sem lugar na planta'} · {execucaoDe(peca)}
                  </span>
                  {peca.sheetNames !== undefined && peca.sheetNames.length > 0 && (
                    <span className="block text-xs text-(--color-content-muted)">
                      na planilha: {peca.sheetNames.join(', ')}
                    </span>
                  )}
                </span>
                {onRenomear !== undefined && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={ocupado}
                    aria-label={`Renomear ${peca.name}`}
                    title="Renomear"
                    onClick={() => {
                      setRenomeando(peca.name);
                      setNomeNovo(peca.name);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={naPlanta ? 'ghost' : 'secondary'}
                  disabled={ocupado || semEscala || posicionando === peca.name}
                  aria-label={`${naPlanta ? 'Mover' : 'Posicionar'} ${peca.name} na planta`}
                  onClick={() => onPosicionar(peca.name)}
                >
                  <Crosshair className="size-4" aria-hidden="true" />
                  {naPlanta ? 'Mover' : 'Posicionar na planta'}
                </Button>
                {naPlanta && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={ocupado}
                    aria-label={`Tirar ${peca.name} da planta`}
                    onClick={() => onTirar(peca.name)}
                  >
                    <X className="size-4" aria-hidden="true" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {erro !== undefined && (
        <p role="alert" className="text-sm text-(--color-state-risk)">
          {erro}
        </p>
      )}
    </section>
  );
}
